import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Printer } from "lucide-react";
import { useGetExamApplicationDetailsQuery } from "../../api/examsApi";
import {
  printResultLetter,
  isInterview,
  isPositiveResult,
  resultLabel,
} from "../../utils/resultLetter";

function fmt(dt?: string | null) {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleString();
  } catch {
    return dt;
  }
}

/**
 * What the candidate still owes for having been moved to a dearer exam, in kobo.
 * 0 where there is no such invoice, or it has been settled.
 */
function balanceOwedKobo(app: any): number {
  const inv = app?.balance_invoice;
  if (!inv || inv.status === "paid") return 0;
  return Math.max(0, Number(inv.balance ?? 0));
}

const naira = (kobo: number) => `₦${(kobo / 100).toLocaleString("en-NG")}`;

export default function ExamApplicationDetailsPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useGetExamApplicationDetailsQuery(
    id,
    { skip: !id }
  );
  // The API only includes `result` once it has been published.
  const result: any = (data as any)?.result ?? null;
  const papers: any[] = ((data as any)?.papers ?? []) as any[];

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      <div className="flex items-start justify-between gap-4">
        <div>
          {/* Back to wherever they came from. The old target was
              /exams/applications, which is members-only — a probationer
              following it from the exam-call list landed on /unauthorized. */}
          <button
            onClick={() => navigate(-1)}
            className="text-sm text-gray-600 hover:underline"
          >
            ← Back
          </button>
          <h1 className="text-xl font-semibold text-gray-900 mt-2">
            Application Details
          </h1>
          <div className="text-sm text-gray-600 mt-1">
            {data?.exam?.title} <span className="text-gray-400">•</span>{" "}
            {data?.diet?.title}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* The same three statuses the API issues a card for
              (ExamAdmissionCardController::CARD_STATUSES). Offering it earlier
              only produced a 403 the candidate could do nothing about. */}
          {data &&
            ["approved", "graded", "completed"].includes(data.status) &&
            !["MRI", "PCI"].includes((data.exam?.code ?? "").toUpperCase()) && (
            <Link
              to={`/exams/applications/${id}/card`}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-900 text-white text-xs font-black uppercase tracking-widest hover:bg-orange-600 transition-all"
            >
              <Printer size={12} /> Admission Card
            </Link>
          )}
          {data?.diet?.session?.id && (
            <Link
              to={`/exams/sessions/${data.diet.session.id}/timetable`}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-orange-500 text-white text-xs font-black uppercase tracking-widest hover:bg-orange-600 transition-all"
            >
              <Printer size={12} /> Timetable
            </Link>
          )}
          <button
            onClick={() => refetch()}
            className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="mt-5">
        {isLoading ? (
          <div className="text-sm text-gray-600">Loading...</div>
        ) : error ? (
          <div className="text-sm text-rose-700">
            Failed to load.
            <button className="ml-2 underline" onClick={() => refetch()}>
              retry
            </button>
          </div>
        ) : !data ? null : (
          <div className="border rounded-2xl p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-semibold text-gray-900">
                  {data.exam?.title}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Code: {data.exam?.code || "—"} • Status: {data.status}
                </div>
              </div>
              <span className="text-xs rounded-full bg-gray-100 text-gray-700 px-2 py-1">
                {data.status}
              </span>
            </div>

            {/* A balance left by being MOVED to a dearer exam. It is kept off
                the member's bills list — an exam fee is not a subscription and
                counting it as dues would put them in arrears — so this screen is
                where they are told about it and where they pay it. */}
            {balanceOwedKobo(data) > 0 && (
              <div className="mt-4 rounded-xl border border-orange-200 bg-orange-50 p-4">
                <div className="text-[11px] font-black uppercase tracking-widest text-orange-500">
                  Balance to pay
                </div>
                <div className="text-lg font-bold text-gray-900 mt-0.5">
                  {naira(balanceOwedKobo(data))}
                </div>
                <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                  Your registration was moved to this exam, which costs more than the one you
                  originally paid for. This is the difference only — what you have already paid
                  stands. Please settle it before the sitting.
                </p>
                <Link
                  to={`/invoice/${(data as any).balance_invoice?.id}`}
                  className="inline-block mt-3 px-4 py-2 rounded-lg bg-orange-500 text-white text-xs font-black uppercase tracking-widest hover:bg-orange-600 transition-all"
                >
                  Pay Balance
                </Link>
              </div>
            )}

            <div className="mt-3 text-sm text-gray-700">
              <div>
                Diet: <span className="font-medium">{data.diet?.title}</span>
              </div>
              <div>
                Center:{" "}
                <span className="font-medium">{data.center?.name || "—"}</span>
              </div>
              <div>
                Submitted:{" "}
                <span className="font-medium">{fmt(data.submitted_at)}</span>
              </div>
            </div>

            {/* What they actually registered for. The payload has carried this
                all along; the page simply never showed it, so a candidate
                could not check their own papers before sitting. */}
            {papers.length > 0 && (
              <div className="mt-4 border-t pt-4">
                <div className="text-[11px] font-black uppercase tracking-widest text-gray-400 mb-2">
                  Papers registered ({papers.length})
                </div>
                <ul className="space-y-1.5">
                  {papers.map((p: any) => (
                    <li key={p.id} className="text-sm text-gray-700 flex items-baseline gap-2">
                      <span className="font-mono text-xs text-gray-400 shrink-0">
                        {p.paper?.code ?? "—"}
                      </span>
                      <span>{p.paper?.title ?? p.paper?.name ?? "Untitled paper"}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Result — only present once the admin has published it */}
            <div className="mt-4 border-t pt-4">
              <div className="text-[11px] font-black uppercase tracking-widest text-gray-400 mb-2">
                Result
              </div>
              {result ? (
                <div className="space-y-2">
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${
                      isPositiveResult(result.overall_status)
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                        : result.overall_status === "partial_pass"
                        ? "bg-amber-50 text-amber-700 border border-amber-100"
                        : result.overall_status === "absent"
                        ? "bg-gray-100 text-gray-600 border border-gray-200"
                        : "bg-rose-50 text-rose-700 border border-rose-100"
                    }`}
                  >
                    {resultLabel(result.overall_status)}
                  </span>
                  {result.papers_sat != null && (
                    <div className="text-xs text-gray-600">
                      {result.papers_passed}/{result.papers_sat} papers passed
                    </div>
                  )}
                  {result.remarks && (
                    <p className="text-xs text-gray-500 italic">{result.remarks}</p>
                  )}
                  {/* Every exam produces a printable document now — an
                      interview's recommendation letter, or a course exam's
                      statement of result. Being inside this branch already means
                      the result was released, which is the only gate. */}
                  <div className="pt-2">
                    <button
                      onClick={() => printResultLetter(data, data.diet)}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-900 text-white text-xs font-black uppercase tracking-widest hover:bg-orange-600 transition-all"
                    >
                      <Printer size={12} />{" "}
                      {isInterview(data.exam)
                        ? "Print Result Letter"
                        : "View / Print Statement of Result"}
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-gray-500">
                  Your result has not been published yet. Please check back later.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
